/**
 * Root application component.
 *
 * Sets up the global providers (Helmet for SEO, MUI theme, auth context,
 * React Router) and defines every client-side route. The layout wraps
 * all pages in a sticky Navbar + Footer shell.
 */

import React from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { ThemeProvider, CssBaseline, Box } from '@mui/material';
import { HelmetProvider } from 'react-helmet-async';
import './i18n';
import theme from './theme';
import { AuthProvider } from './contexts/AuthContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import SubmitProjectPage from './pages/SubmitProjectPage';
import ShowcasePage from './pages/ShowcasePage';
import DashboardPage from './pages/DashboardPage';
import AdminPage from './pages/AdminPage';
import TranslationManagementPage from './pages/TranslationManagementPage';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import BlogManagementPage from './pages/BlogManagementPage';
import ShowcaseManagementPage from './pages/ShowcaseManagementPage';
import ProjectDetailPage from './pages/ProjectDetailPage';

/** Top-level component that composes global providers and route definitions. */
function App() {
  return (
    <HelmetProvider>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <BrowserRouter>
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <Navbar />
            <Box component="main" sx={{ flexGrow: 1 }}>
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/submit-project" element={<SubmitProjectPage />} />
                <Route path="/showcase" element={<ShowcasePage />} />
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/projects/:id" element={<ProjectDetailPage />} />
                <Route path="/admin" element={<AdminPage />} />
                <Route path="/admin/translations" element={<TranslationManagementPage />} />
                <Route path="/admin/blog" element={<BlogManagementPage />} />
                <Route path="/admin/showcase" element={<ShowcaseManagementPage />} />
                <Route path="/blog" element={<BlogPage />} />
                <Route path="/blog/:slug" element={<BlogPostPage />} />
              </Routes>
            </Box>
            <Footer />
          </Box>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
